import { DunningBanner } from '@/components/DunningBanner';
import { NpsSurveyModal } from '@/components/NpsSurveyModal';
import { TrialBanner } from '@/components/TrialBanner';
import { UpgradeModal } from '@/components/UpgradeModal';
import { CommandPalette } from '@/components/command-palette';
import { KeyboardShortcutsDialog } from '@/components/keyboard-shortcuts-dialog';
import { useBillingSubscription } from '@/hooks/use-billing';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useWebSocket } from '@/hooks/use-websocket';
import { useDensityStore } from '@/stores/density.store';
import { useKeyboardShortcutsDialogStore } from '@/stores/keyboard-shortcuts.store';
import { useThemeStore } from '@/stores/theme.store';
// apps/dashboard/src/components/layout/app-layout.tsx
import { Button, DensityProvider, fadeUp } from '@ominiflow/ui';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Header } from './header';
import { Sidebar } from './sidebar';

export function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const openShortcuts = useKeyboardShortcutsDialogStore((s) => s.setOpen);
  const density = useDensityStore((s) => s.density);

  // Start the WebSocket connection for the authenticated user
  useWebSocket();

  // Ensure theme is applied from store on mount (prevents flash on rehydrate)
  const { theme } = useThemeStore();
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark', 'light');
    root.classList.add(theme);
  }, [theme]);

  // Global shortcut: ? → show keyboard shortcuts dialog
  useKeyboardShortcuts([
    { key: '?', shift: true, handler: () => openShortcuts(true), ignoreGuard: false },
  ]);

  const { data: sub } = useBillingSubscription();
  const showTrialBanner = sub?.status === 'TRIAL' && Boolean(sub.trialEndsAt);

  return (
    <DensityProvider density={density} className="flex h-screen overflow-hidden bg-background">
      {/* Skip navigation — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-9999 focus:left-4 focus:top-4 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:ring-2 focus:ring-ring"
      >
        Pular para conteúdo principal
      </a>

      {/* Mobile sidebar toggle */}
      <div className="absolute left-3 top-3.5 z-50 md:hidden">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Alternar menu lateral"
          onClick={() => setSidebarCollapsed((p) => !p)}
        >
          <Menu className="h-4 w-4" />
        </Button>
      </div>

      {/* Sidebar — hover-expand handled internally; collapsed = pinned-collapsed */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
      />

      <div className="flex flex-1 flex-col min-w-0">
        {showTrialBanner && (
          <TrialBanner
            trialEndsAt={sub?.trialEndsAt!}
            onUpgradeClick={() => setUpgradeOpen(true)}
          />
        )}
        <DunningBanner />
        <Header />
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-auto outline-none">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={useRouterState({ select: (s) => s.location.pathname })}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="h-full"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        currentPlan={sub?.planCode ?? 'FREE'}
      />

      {/* NPS in-app survey — appears once per 90 days after activation */}
      <NpsSurveyModal />
      <CommandPalette />
      <KeyboardShortcutsDialog />
    </DensityProvider>
  );
}
